import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getRowHeightBasedOnContent } from '@client-shared/ui-agGrid';

import Config from '../../config';
import AgGrid from '../../shared/agGrid/agGrid.component';
import {
  setTrialBalanceFilter,
  unsetTrialBalanceFilter,
  uploadTrialBalanceFile,
} from './store/actions';
import SearchBar from '../../shared/searchBar/searchBar.component';
import ValidationSummaryHeader from '../../shared/validationSummaryHeader.component';
import JimJobInfo from '../../shared/jim/jimJobInfo.component';
import {
  importValidationColumnDefinition,
} from '../../shared/columnDefinitions';
import withPillNavigation from '../../shared/withPillNavigation.decorator';
import {
  alignCenterCellStyle,
} from '@common/cellStyles';
import {
  trialBalanceColumnDefsTpc,
  trialBalanceColumnDefsEsdrm,
} from '../../shared/columnDefinitions/trialBalance';
import { YES_FLAG } from '../../shared/constants';
import { fetchProvisionData } from '../../shared/store/actions';
import {
  isLastJobOfTypeFinishedSelectorFactory,
  progressOfLastJobOfTypeSelectorFactory,
} from '../../shared/jim/store/selectors';
import { companyCodeSelector, fetchingPriovisionDataSelector } from '../../shared/store/selectors';
import { trialBalancePropTypes } from '../propTypes';

const progressSelector = progressOfLastJobOfTypeSelectorFactory(Config.jimJobs.trialBalanceImport.type);
const isLastJobOfTypeFinishedSelector = isLastJobOfTypeFinishedSelectorFactory(Config.jimJobs.trialBalanceImport.type);

const TrialBalancePillTypes = {
  GAAP: 'GAAP',
  GAAP_TO_STAT: 'GAAP_TO_STAT',
  STAT: 'STAT',
};

const TrialBalancePillDefinitions = [
  { label: 'GAAP', type: TrialBalancePillTypes.GAAP },
  { label: 'GAAP to STAT', type: TrialBalancePillTypes.GAAP_TO_STAT },
  { label: 'STAT', type: TrialBalancePillTypes.STAT },
];

const defaultColumnDefs = {
  ...alignCenterCellStyle,
  width: 100,
};

class TrialBalanceTab extends PureComponent {
  static propTypes = {
    activePill: PropTypes.oneOf(Object.values(TrialBalancePillTypes)).isRequired,
    chartName: PropTypes.string.isRequired,
    trialBalanceCollection: PropTypes.arrayOf(trialBalancePropTypes).isRequired,
    trialBalanceFilter: PropTypes.string.isRequired,
    setTrialBalanceFilter: PropTypes.func.isRequired,
    unsetTrialBalanceFilter: PropTypes.func.isRequired,
    trialBalanceStatus: PropTypes.shape({
      fileName: PropTypes.string,
      importStatus: PropTypes.number.isRequired,
      importResults: PropTypes.string.isRequired,
      importFileRowCount: PropTypes.number.isRequired,
      trialTotal: PropTypes.number.isRequired,
      chartName: PropTypes.string.isRequired,
      chartVersion: PropTypes.number.isRequired,
    }),
    trialBalanceUploadProgress: PropTypes.number,
    isUploadingTrialBalance: PropTypes.bool,
    isProcessingTrialBalance: PropTypes.bool,
    fetchingProvisionData: PropTypes.bool,
    fetchProvisionData: PropTypes.func,
    companyCode: PropTypes.string,
  };

  filterByPillType = (collection, activePill) => {
    if (activePill === TrialBalancePillTypes.GAAP) {
      return collection.filter(({ BOOK_TYPE }) => BOOK_TYPE.toLowerCase() === 'p'); // eslint-disable-line camelcase
    }

    if (activePill === TrialBalancePillTypes.GAAP_TO_STAT) {
      return collection.filter(({ BOOK_TYPE }) => BOOK_TYPE.toLowerCase() === 'g'); // eslint-disable-line camelcase
    }

    return collection;
  };

  onRowDataUpdated = params => {
    if (params.gridApi) {
      params.gridApi.applyColumnState({
        state: [
          { colId: 'IMPORT_VALIDATION', sort: 'desc' },
        ],
      });
      params.gridApi.autoSizeAllColumns();
    }
  };

  fetchProvisionData = () => {
    const { fetchProvisionData, companyCode } = this.props;

    fetchProvisionData(companyCode);
  };

  render() {
    const {
      activePill,
      chartName,
      trialBalanceCollection,
      trialBalanceFilter,
      trialBalanceStatus,
    } = this.props;
    const filteredTrialBalanceCollection = this.filterByPillType(trialBalanceCollection, activePill);
    const hasImportValidationsErrors = filteredTrialBalanceCollection
      .some(trialBalance => trialBalance.IMPORT_STATUS_DETAIL === -1);

    const columnDefs = chartName === 'R2R' ? trialBalanceColumnDefsEsdrm : trialBalanceColumnDefsTpc;

    const isLoading = this.props.fetchingProvisionData
      || this.props.isUploadingTrialBalance
      || this.props.isProcessingTrialBalance;

    return (
      <div className="trial-balance-tab">
        <JimJobInfo
          jobConfig={Config.jimJobs.trialBalanceImport}
          showProgressBar
          onJobComplete={this.fetchProvisionData}
        />
        {filteredTrialBalanceCollection.length
          ? (
            <span>
              <ValidationSummaryHeader
                className="trial-balance-tab-header"
                entityName="Trial Balance"
                validationSummary={trialBalanceStatus}
                shouldDisplayZero
                additionalComponent={
                  <div className="col-2">
                    <SearchBar
                      onCancel={this.props.unsetTrialBalanceFilter}
                      onSearch={this.props.setTrialBalanceFilter}
                      value={this.props.trialBalanceFilter}
                    />
                  </div>
                }
              />
              <div
                className="trial-balance-grid"
              >
                <AgGrid
                  columnDefs={
                    hasImportValidationsErrors
                      ? [importValidationColumnDefinition].concat(columnDefs)
                      : columnDefs
                  }
                  defaultColDef={defaultColumnDefs}
                  rowData={filteredTrialBalanceCollection}
                  quickFilterText={trialBalanceFilter}
                  onRowDataUpdated={this.onRowDataUpdated}
                  getRowHeight={getRowHeightBasedOnContent({
                    field: 'IMPORT_VALIDATION',
                    margins: 8,
                    lineHeight: 17,
                    extraLength: 12,
                    averageCharsInLine: 30,
                  })}
                  isGridLoading={isLoading}
                />
              </div>
            </span>
          ) : (
            <div className="trial-balance-not-loaded">
              <div className="trial-balance-not-loaded-text">
                A Trial Balance File Has Not Been Loaded
              </div>
            </div>
          )}
      </div>
    );
  }
}

export default connect(
  state => ({
    companyCode: companyCodeSelector(state),
    trialBalanceCollection: state.provision.trialBalance.trialBalanceCollection,
    trialBalanceFilter: state.provision.trialBalance.trialBalanceFilter,
    statCalc: state.provision.general.companyCodeDetails.statCalc,
    trialBalanceStatus: state.provision.trialBalance.trialBalanceStatus,
    isUploadingTrialBalance: state.provision.general.isUploadingTrialBalance,
    trialBalanceUploadProgress: progressSelector(state),
    isProcessingTrialBalance: !isLastJobOfTypeFinishedSelector(state),
    fetchingProvisionData: fetchingPriovisionDataSelector(state),
  }),
  {
    setTrialBalanceFilter,
    unsetTrialBalanceFilter,
    uploadTrialBalanceFile,
    fetchProvisionData,
  }
)(
  withPillNavigation(
    ({ statCalc }) => {
      if (statCalc === YES_FLAG) {
        return TrialBalancePillDefinitions;
      }

      return TrialBalancePillDefinitions.filter(
        pill => pill.type === TrialBalancePillTypes.GAAP
      );
    },
    {
      propTypes: {
        statCalc: PropTypes.string.isRequired,
      },
    }
  )(TrialBalanceTab)
);
